import { useState, useEffect, type ReactNode } from 'react'
import { CheckCircle2, Loader, Plus, X } from 'lucide-react'
import LocationCard from './LocationCard'
import { updateCustomerSettings, updateLocationSettings } from '../../services/settingsApi'
import type { CustomerConfig } from '../../types/dashboard'

// ─── Types ────────────────────────────────────────────────────────────────────

interface SettingsLocation {
  id: string
  name: string
  address: string | null
  has_google_credentials: boolean
  notification_email: string | null
  fetch_frequency: string | null
  brand_voice: string | null
  auto_respond_enabled: boolean
  platform_connections: Array<{ platform: string; shop_domain: string | null }>
  brief_enabled: boolean
  brief_day_of_week: number
  brief_hour: number
  brief_timezone: string
}

interface SettingsData {
  business_name: string
  email: string
  plan: string
  api_key: string
  config: CustomerConfig
  locations: SettingsLocation[]
}

interface Toast {
  message: string
  type: 'success' | 'danger' | 'info'
}

export interface SettingsPageProps {
  data: SettingsData
  selectedLocationId: string
  onSettingsSaved: () => void
  onToast: (t: Toast) => void
  onGetReviews: (locationId: string) => Promise<number>
  onCredentialsSaved: () => void
  onJudgeMeConnected: () => void
  onJudgeMeDisconnected: () => void
}

// ─── Form interfaces ──────────────────────────────────────────────────────────

interface AccountForm {
  business_name: string
  response_length: 'short' | 'medium' | 'detailed'
  min_score: number
}

interface AutoForm {
  positive: boolean
  neutral: boolean
  negative: boolean
}

interface LocForm {
  notification_email: string
  brand_voice: string
}

interface BriefForm {
  enabled: boolean
  day_of_week: number
  hour: number
  timezone: string
}

// ─── Static config ────────────────────────────────────────────────────────────

const MAX_EXAMPLES = 15

const RESPONSE_LENGTHS = ['short', 'medium', 'detailed'] as const

const RESPONSE_LENGTH_HINT: Record<string, string> = {
  short: '30-50 words',
  medium: '50-80 words',
  detailed: '100-150 words',
}

const AUTO_ROWS: Array<{ label: string; sub: string; key: keyof AutoForm }> = [
  { label: 'Positive reviews', sub: '4-5 stars', key: 'positive' },
  { label: 'Neutral reviews', sub: '3 stars', key: 'neutral' },
  { label: 'Negative reviews', sub: '1-2 stars', key: 'negative' },
]

const BRIEF_ELIGIBLE_PLANS = ['pro', 'agency', 'enterprise']

const DAY_OPTIONS = [
  { label: 'Monday', value: 0 },
  { label: 'Tuesday', value: 1 },
  { label: 'Wednesday', value: 2 },
  { label: 'Thursday', value: 3 },
  { label: 'Friday', value: 4 },
  { label: 'Saturday', value: 5 },
  { label: 'Sunday', value: 6 },
]

const HOUR_OPTIONS = Array.from({ length: 24 }, (_, hour) => ({
  value: hour,
  label:
    hour === 0 ? '12:00 AM' : hour < 12 ? `${hour}:00 AM` : hour === 12 ? '12:00 PM' : `${hour - 12}:00 PM`,
}))

const TIMEZONE_OPTIONS = [
  { label: 'Eastern Time', value: 'America/New_York' },
  { label: 'Central Time', value: 'America/Chicago' },
  { label: 'Mountain Time', value: 'America/Denver' },
  { label: 'Pacific Time', value: 'America/Los_Angeles' },
  { label: 'Alaska Time', value: 'America/Anchorage' },
  { label: 'Hawaii Time', value: 'Pacific/Honolulu' },
  { label: 'UTC', value: 'UTC' },
  { label: 'India Standard Time', value: 'Asia/Kolkata' },
]

// ─── Primitives ───────────────────────────────────────────────────────────────

function Toggle({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={`relative w-10 h-[22px] rounded-full transition-colors flex-shrink-0 focus:outline-none ${
        checked ? '' : 'bg-white/10'
      }`}
      style={checked ? { background: 'linear-gradient(135deg, #4ade80, #22d3ee)' } : undefined}
    >
      <span
        className={`absolute top-[3px] left-[3px] w-4 h-4 rounded-full bg-white shadow-sm transition-transform ${
          checked ? 'translate-x-[18px]' : 'translate-x-0'
        }`}
      />
    </button>
  )
}

function SaveBtn({
  isDirty,
  isSaving,
  savedOk,
  onClick,
}: {
  isDirty: boolean
  isSaving: boolean
  savedOk: boolean
  onClick: () => void
}) {
  const active = isDirty && !savedOk
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!active || isSaving}
      className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-[13px] font-medium transition-all ${
        savedOk
          ? 'bg-rd-accent/10 text-rd-accent'
          : active
          ? 'text-[#0a0a0f]'
          : 'text-rd-dim bg-white/[0.04] cursor-not-allowed'
      }`}
      style={active ? { background: 'linear-gradient(135deg, #4ade80, #22d3ee)' } : undefined}
    >
      {isSaving ? (
        <><Loader className="w-3.5 h-3.5 animate-spin" />Saving…</>
      ) : savedOk ? (
        <><CheckCircle2 className="w-3.5 h-3.5" />Saved</>
      ) : 'Save changes'}
    </button>
  )
}

function FieldLabel({ htmlFor, children }: { htmlFor?: string; children: ReactNode }) {
  return (
    <label htmlFor={htmlFor} className="block text-[13px] font-medium text-rd-muted mb-1.5">
      {children}
    </label>
  )
}

function Hint({ children }: { children: ReactNode }) {
  return <p className="text-[11px] text-rd-muted mt-1.5">{children}</p>
}

function SectionCard({
  title,
  subtitle,
  children,
  footer,
}: {
  title: string
  subtitle: string
  children: ReactNode
  footer: ReactNode
}) {
  return (
    <div className="bg-rd-surface border border-white/[0.07] rounded-2xl overflow-hidden">
      <div className="px-6 py-4 border-b border-white/[0.06]">
        <h2 className="font-sora font-semibold text-[15px] text-rd-text">{title}</h2>
        <p className="text-[12px] text-rd-muted mt-0.5">{subtitle}</p>
      </div>
      <div className="px-6 py-5 space-y-5">{children}</div>
      <div className="px-6 py-3 border-t border-white/[0.06] flex justify-end">{footer}</div>
    </div>
  )
}

// ─── SettingsPage ─────────────────────────────────────────────────────────────

export default function SettingsPage({
  data,
  selectedLocationId,
  onSettingsSaved,
  onToast,
  onGetReviews,
  onCredentialsSaved,
  onJudgeMeConnected,
  onJudgeMeDisconnected,
}: SettingsPageProps) {
  const selectedLoc = data.locations.find((l) => l.id === selectedLocationId) ?? null
  const judgeMeConn = selectedLoc?.platform_connections?.find((c) => c.platform === 'judgeme')

  // ── Account ────────────────────────────────────────────────────────────────

  const cfg = data.config ?? {}

  const initAccount = (): AccountForm => ({
    business_name: data.business_name,
    response_length: RESPONSE_LENGTHS.includes(
      cfg.response_length as (typeof RESPONSE_LENGTHS)[number],
    )
      ? (cfg.response_length as AccountForm['response_length'])
      : 'medium',
    min_score: cfg.min_validation_score ?? 70,
  })

  const [accountForm, setAccountForm] = useState<AccountForm>(initAccount)
  const [savedAccount, setSavedAccount] = useState<AccountForm>(initAccount)
  const [savingAccount, setSavingAccount] = useState(false)
  const [accountOk, setAccountOk] = useState(false)

  const accountDirty = JSON.stringify(accountForm) !== JSON.stringify(savedAccount)

  const saveAccount = async () => {
    setSavingAccount(true)
    try {
      await updateCustomerSettings(data.api_key, {
        business_name: accountForm.business_name,
        config: {
          response_length: accountForm.response_length,
          min_validation_score: accountForm.min_score,
        },
      })
      setSavedAccount({ ...accountForm })
      setAccountOk(true)
      setTimeout(() => setAccountOk(false), 2000)
      onSettingsSaved()
    } catch (err) {
      onToast({ message: err instanceof Error ? err.message : 'Failed to save', type: 'danger' })
    } finally {
      setSavingAccount(false)
    }
  }

  // ── Auto-respond ───────────────────────────────────────────────────────────

  const initAuto = (): AutoForm => ({
    positive: cfg.auto_respond_positive ?? false,
    neutral: cfg.auto_respond_neutral ?? false,
    negative: cfg.auto_respond_negative ?? false,
  })

  const [autoForm, setAutoForm] = useState<AutoForm>(initAuto)
  const [savedAuto, setSavedAuto] = useState<AutoForm>(initAuto)
  const [savingAuto, setSavingAuto] = useState(false)
  const [autoOk, setAutoOk] = useState(false)

  const autoDirty = JSON.stringify(autoForm) !== JSON.stringify(savedAuto)

  const saveAuto = async () => {
    setSavingAuto(true)
    try {
      await updateCustomerSettings(data.api_key, {
        config: {
          auto_respond_positive: autoForm.positive,
          auto_respond_neutral: autoForm.neutral,
          auto_respond_negative: autoForm.negative,
        },
      })
      setSavedAuto({ ...autoForm })
      setAutoOk(true)
      setTimeout(() => setAutoOk(false), 2000)
      onSettingsSaved()
    } catch (err) {
      onToast({ message: err instanceof Error ? err.message : 'Failed to save', type: 'danger' })
    } finally {
      setSavingAuto(false)
    }
  }

  // ── Location ───────────────────────────────────────────────────────────────

  const getLocForm = (locId: string): LocForm => {
    const loc = data.locations.find((l) => l.id === locId)
    return {
      notification_email: loc?.notification_email ?? '',
      brand_voice: loc?.brand_voice ?? '',
    }
  }

  const [locForm, setLocForm] = useState<LocForm>(() => getLocForm(selectedLocationId))
  const [savedLoc, setSavedLoc] = useState<LocForm>(() => getLocForm(selectedLocationId))
  const [savingLoc, setSavingLoc] = useState(false)
  const [locOk, setLocOk] = useState(false)

  // Re-init location form when the selected location changes
  useEffect(() => {
    const form = getLocForm(selectedLocationId)
    setLocForm(form)
    setSavedLoc(form)
  }, [selectedLocationId])

  const locDirty = JSON.stringify(locForm) !== JSON.stringify(savedLoc)

  const saveLoc = async () => {
    if (!selectedLoc) return
    setSavingLoc(true)
    try {
      await updateLocationSettings(data.api_key, selectedLocationId, {
        notification_email: locForm.notification_email || undefined,
        brand_voice: locForm.brand_voice || undefined,
      })
      setSavedLoc({ ...locForm })
      setLocOk(true)
      setTimeout(() => setLocOk(false), 2000)
      onSettingsSaved()
    } catch (err) {
      onToast({ message: err instanceof Error ? err.message : 'Failed to save', type: 'danger' })
    } finally {
      setSavingLoc(false)
    }
  }

  // ── Weekly Reputation Brief ────────────────────────────────────────────────

  const briefEligible = BRIEF_ELIGIBLE_PLANS.includes(data.plan)

  const getBriefForm = (locId: string): BriefForm => {
    const loc = data.locations.find((l) => l.id === locId)
    return {
      enabled: loc?.brief_enabled ?? true,
      day_of_week: loc?.brief_day_of_week ?? 5,
      hour: loc?.brief_hour ?? 9,
      timezone: loc?.brief_timezone ?? 'America/New_York',
    }
  }

  const [briefForm, setBriefForm] = useState<BriefForm>(() => getBriefForm(selectedLocationId))
  const [savedBrief, setSavedBrief] = useState<BriefForm>(() => getBriefForm(selectedLocationId))
  const [savingBrief, setSavingBrief] = useState(false)
  const [briefOk, setBriefOk] = useState(false)

  // Re-init brief form when the selected location changes
  useEffect(() => {
    const form = getBriefForm(selectedLocationId)
    setBriefForm(form)
    setSavedBrief(form)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocationId])

  const briefDirty = JSON.stringify(briefForm) !== JSON.stringify(savedBrief)

  const saveBrief = async () => {
    if (!selectedLoc) return
    setSavingBrief(true)
    try {
      await updateLocationSettings(data.api_key, selectedLocationId, {
        brief_enabled: briefForm.enabled,
        brief_day_of_week: briefForm.day_of_week,
        brief_hour: briefForm.hour,
        brief_timezone: briefForm.timezone,
      })
      setSavedBrief({ ...briefForm })
      setBriefOk(true)
      setTimeout(() => setBriefOk(false), 2000)
      onSettingsSaved()
    } catch (err) {
      onToast({ message: err instanceof Error ? err.message : 'Failed to save', type: 'danger' })
    } finally {
      setSavingBrief(false)
    }
  }

  // ── Example responses ─────────────────────────────────────────────────────

  const initExamples = (): string[] =>
    Array.isArray(cfg.example_responses) ? [...cfg.example_responses] : []

  const [examples, setExamples] = useState<string[]>(initExamples)
  const [savedExamples, setSavedExamples] = useState<string[]>(initExamples)
  const [savingExamples, setSavingExamples] = useState(false)
  const [examplesOk, setExamplesOk] = useState(false)

  const examplesDirty = JSON.stringify(examples) !== JSON.stringify(savedExamples)

  const saveExamples = async () => {
    setSavingExamples(true)
    try {
      const clean = examples.map((e) => e.trim()).filter(Boolean)
      await updateCustomerSettings(data.api_key, {
        config: { example_responses: clean },
      })
      setExamples(clean)
      setSavedExamples(clean)
      setExamplesOk(true)
      setTimeout(() => setExamplesOk(false), 2000)
      onSettingsSaved()
    } catch (err) {
      onToast({ message: err instanceof Error ? err.message : 'Failed to save', type: 'danger' })
    } finally {
      setSavingExamples(false)
    }
  }

  const addExample = () => setExamples((prev) => [...prev, ''])
  const removeExample = (i: number) => setExamples((prev) => prev.filter((_, j) => j !== i))
  const updateExample = (i: number, val: string) =>
    setExamples((prev) => prev.map((e, j) => (j === i ? val : e)))

  const filledCount = examples.filter((e) => e.trim()).length

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="flex-1 overflow-y-auto px-6 py-8">
      <div className="max-w-2xl space-y-6">

        {/* Page header */}
        <div>
          <h1 className="font-sora font-bold text-xl text-rd-text mb-1">Settings</h1>
          <p className="text-rd-muted text-sm">Customize how ReputeDesk handles your reviews.</p>
        </div>

        {/* Account */}
        <SectionCard
          title="Account"
          subtitle="Business details used in AI-generated responses"
          footer={
            <SaveBtn
              isDirty={accountDirty}
              isSaving={savingAccount}
              savedOk={accountOk}
              onClick={saveAccount}
            />
          }
        >
          <div>
            <FieldLabel htmlFor="setting-biz-name">Display Name</FieldLabel>
            <input
              id="setting-biz-name"
              type="text"
              value={accountForm.business_name}
              onChange={(e) => setAccountForm((f) => ({ ...f, business_name: e.target.value }))}
              className="reg-input text-sm"
              placeholder="Your business name"
            />
            <Hint>Appears in all AI-generated response text.</Hint>
          </div>

          <div>
            <FieldLabel>Response Length</FieldLabel>
            <div className="flex gap-2">
              {RESPONSE_LENGTHS.map((len) => (
                <button
                  key={len}
                  type="button"
                  onClick={() => setAccountForm((f) => ({ ...f, response_length: len }))}
                  className={`px-3 py-1.5 rounded-lg text-[13px] font-medium border transition-colors capitalize ${
                    accountForm.response_length === len
                      ? 'bg-rd-accent/10 text-rd-accent border-rd-accent/30'
                      : 'text-rd-muted border-white/[0.07] hover:border-white/20'
                  }`}
                >
                  {len}
                </button>
              ))}
            </div>
            <Hint>
              {RESPONSE_LENGTHS.map((l) => RESPONSE_LENGTH_HINT[l]).join(' · ')}
            </Hint>
          </div>

          <div>
            <FieldLabel htmlFor="setting-min-score">
              Minimum Quality Score —{' '}
              <span className="text-rd-text font-semibold">{accountForm.min_score}</span>
            </FieldLabel>
            <input
              id="setting-min-score"
              type="range"
              min={0}
              max={100}
              step={5}
              value={accountForm.min_score}
              onChange={(e) =>
                setAccountForm((f) => ({ ...f, min_score: Number(e.target.value) }))
              }
              className="w-full accent-rd-accent"
            />
            <div className="flex justify-between text-[11px] text-rd-muted mt-1">
              <span>0 — always post</span>
              <span>100 — never post</span>
            </div>
          </div>
        </SectionCard>

        {/* Response Automation */}
        <SectionCard
          title="Response Automation"
          subtitle="Control when AI responses are automatically posted to Google"
          footer={
            <SaveBtn
              isDirty={autoDirty}
              isSaving={savingAuto}
              savedOk={autoOk}
              onClick={saveAuto}
            />
          }
        >
          <div className="space-y-1">
            {AUTO_ROWS.map(({ label, sub, key }) => (
              <div
                key={key}
                className="flex items-center justify-between py-2.5 border-b border-white/[0.05] last:border-0"
              >
                <div>
                  <p className="text-[13px] text-rd-text">{label}</p>
                  <p className="text-[11px] text-rd-muted">{sub}</p>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-[12px] text-rd-muted">
                    {autoForm[key] ? 'Auto-post' : 'Request approval'}
                  </span>
                  <Toggle
                    checked={autoForm[key]}
                    onChange={(v) => setAutoForm((f) => ({ ...f, [key]: v }))}
                  />
                </div>
              </div>
            ))}
          </div>
          <Hint>
            When off, an approval email is sent to your notification address before the response is posted.
          </Hint>
        </SectionCard>

        {/* Location */}
        {selectedLoc && (
          <SectionCard
            title={selectedLoc.name}
            subtitle="Notification and response settings for this location"
            footer={
              <SaveBtn
                isDirty={locDirty}
                isSaving={savingLoc}
                savedOk={locOk}
                onClick={saveLoc}
              />
            }
          >
            <div>
              <FieldLabel htmlFor="setting-notif-email">Notification Email</FieldLabel>
              <input
                id="setting-notif-email"
                type="email"
                value={locForm.notification_email}
                onChange={(e) => setLocForm((f) => ({ ...f, notification_email: e.target.value }))}
                className="reg-input text-sm"
                placeholder={data.email}
              />
              <Hint>Approval emails are sent here. Defaults to your account email when empty.</Hint>
            </div>

            <div>
              <FieldLabel htmlFor="setting-brand-voice">Brand Voice</FieldLabel>
              <textarea
                id="setting-brand-voice"
                value={locForm.brand_voice}
                onChange={(e) => setLocForm((f) => ({ ...f, brand_voice: e.target.value }))}
                rows={3}
                className="reg-input text-sm resize-none"
                placeholder="e.g. Warm, friendly and professional. Always end with an invitation to return."
              />
              <Hint>Describes the tone and style used in AI-generated responses for this location.</Hint>
            </div>
          </SectionCard>
        )}

        {/* Weekly Reputation Brief */}
        {selectedLoc && briefEligible && (
          <SectionCard
            title="Weekly Reputation Brief"
            subtitle="A recurring email summarizing rating trends, praise, and complaints for this location"
            footer={
              <SaveBtn
                isDirty={briefDirty}
                isSaving={savingBrief}
                savedOk={briefOk}
                onClick={saveBrief}
              />
            }
          >
            <div className="flex items-center justify-between py-1">
              <div>
                <p className="text-[13px] text-rd-text">Send weekly brief</p>
                <p className="text-[11px] text-rd-muted">Delivered by email on the schedule below</p>
              </div>
              <Toggle
                checked={briefForm.enabled}
                onChange={(v) => setBriefForm((f) => ({ ...f, enabled: v }))}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <FieldLabel htmlFor="setting-brief-day">Day</FieldLabel>
                <select
                  id="setting-brief-day"
                  value={briefForm.day_of_week}
                  onChange={(e) =>
                    setBriefForm((f) => ({ ...f, day_of_week: Number(e.target.value) }))
                  }
                  disabled={!briefForm.enabled}
                  className="reg-input text-sm disabled:opacity-50"
                >
                  {DAY_OPTIONS.map((d) => (
                    <option key={d.value} value={d.value}>{d.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <FieldLabel htmlFor="setting-brief-hour">Time</FieldLabel>
                <select
                  id="setting-brief-hour"
                  value={briefForm.hour}
                  onChange={(e) => setBriefForm((f) => ({ ...f, hour: Number(e.target.value) }))}
                  disabled={!briefForm.enabled}
                  className="reg-input text-sm disabled:opacity-50"
                >
                  {HOUR_OPTIONS.map((h) => (
                    <option key={h.value} value={h.value}>{h.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <FieldLabel htmlFor="setting-brief-tz">Timezone</FieldLabel>
              <select
                id="setting-brief-tz"
                value={briefForm.timezone}
                onChange={(e) => setBriefForm((f) => ({ ...f, timezone: e.target.value }))}
                disabled={!briefForm.enabled}
                className="reg-input text-sm disabled:opacity-50"
              >
                {TIMEZONE_OPTIONS.map((tz) => (
                  <option key={tz.value} value={tz.value}>{tz.label}</option>
                ))}
              </select>
              <Hint>Defaults to Saturday morning. Change the day, time, or timezone above.</Hint>
            </div>
          </SectionCard>
        )}

        {selectedLoc && !briefEligible && (
          <SectionCard
            title="Weekly Reputation Brief"
            subtitle="A recurring email summarizing rating trends, praise, and complaints for this location"
            footer={<span className="text-[12px] text-rd-muted">Available on Growth plan and above</span>}
          >
            <p className="text-[13px] text-rd-muted">
              Upgrade to Growth, Agency, or Enterprise to receive a weekly email digest of rating movement,
              what customers loved, and what needs attention for this location.
            </p>
          </SectionCard>
        )}

        {/* Example Responses */}
        <SectionCard
          title="Example Responses"
          subtitle="Upload 4-5 star responses you've written — the AI will match your exact tone for positive reviews"
          footer={
            <div className="flex items-center justify-between w-full">
              <span className="text-[12px] text-rd-muted">
                {filledCount} / {MAX_EXAMPLES} examples
              </span>
              <SaveBtn
                isDirty={examplesDirty}
                isSaving={savingExamples}
                savedOk={examplesOk}
                onClick={saveExamples}
              />
            </div>
          }
        >
          {examples.length === 0 ? (
            <div className="flex flex-col items-center gap-3 py-6 border border-dashed border-white/10 rounded-xl text-center">
              <p className="text-[13px] text-rd-muted max-w-xs">
                No examples yet. Add real responses you've written and the AI will mimic your style on 4-5 star reviews.
              </p>
              <button
                type="button"
                onClick={addExample}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[13px] font-medium border border-white/10 text-rd-muted hover:border-white/20 hover:text-rd-text transition-colors"
              >
                <Plus className="w-3.5 h-3.5" />
                Add your first example
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {examples.map((ex, i) => (
                <div key={i} className="flex gap-2 items-start">
                  <div className="flex-shrink-0 w-5 h-5 mt-2.5 rounded-full bg-white/[0.06] flex items-center justify-center text-[10px] text-rd-dim font-medium">
                    {i + 1}
                  </div>
                  <textarea
                    value={ex}
                    onChange={(e) => updateExample(i, e.target.value)}
                    rows={3}
                    className="reg-input text-sm resize-none flex-1"
                    placeholder="Paste or type a response you'd be happy to see on Google…"
                  />
                  <button
                    type="button"
                    onClick={() => removeExample(i)}
                    className="flex-shrink-0 mt-2.5 text-rd-dim hover:text-danger-500 transition-colors"
                    aria-label="Remove example"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}

              {examples.length < MAX_EXAMPLES && (
                <button
                  type="button"
                  onClick={addExample}
                  className="inline-flex items-center gap-1.5 text-[13px] text-rd-muted hover:text-rd-text transition-colors mt-1"
                >
                  <Plus className="w-3.5 h-3.5" />
                  Add another example
                </button>
              )}
            </div>
          )}
          <p className="text-[11px] text-rd-muted">
            Aim for 10–15 examples covering different situations. Only applied to 4-5 star reviews.
          </p>
        </SectionCard>

        {/* Connections */}
        {selectedLoc && (
          <div>
            <div className="mb-3">
              <h2 className="font-sora font-semibold text-[15px] text-rd-text">Connections</h2>
              <p className="text-[12px] text-rd-muted mt-0.5">
                Manage integrations for {selectedLoc.name}.
              </p>
            </div>
            <LocationCard
              locationId={selectedLoc.id}
              locationName={selectedLoc.name}
              notificationEmail={selectedLoc.notification_email ?? data.email}
              planName={data.plan}
              hasGoogleCredentials={selectedLoc.has_google_credentials}
              hasJudgeMeConnection={!!judgeMeConn}
              judgeMeShopDomain={judgeMeConn?.shop_domain ?? null}
              onGetReviews={() => onGetReviews(selectedLoc.id)}
              onCredentialsSaved={onCredentialsSaved}
              onJudgeMeConnected={onJudgeMeConnected}
              onJudgeMeDisconnected={onJudgeMeDisconnected}
            />
          </div>
        )}

      </div>
    </div>
  )
}
